import React, { type Dispatch, type SetStateAction } from 'react';
import Image from 'next/image';
import { SingleValue } from 'react-select';

import locationPin from '@assets/images/locationPinRedesign.svg';
import calendarHeaderIcon from '@assets/images/calendarRedesign.svg';
import { Location } from '@components/Hotels/types';

import SearchSpinner from '@components/common/SearchSpinner/SearchSpinner';
import ReactSelectSearch from '@components/common/ReactSelectSearch/ReactSelectSearchRedesign';
import ReactDatePicker from '@components/common/ReactDatePickerLandingPage/ReactDatePicker';
import { SearchOption } from '@components/common/SearchBarVariant';

import Button from '../Button';
import styles from '../pure.module.css';

type SearchBarRedesignProps = {
  isLoading: boolean;
  value: SingleValue<SearchOption> | undefined;
  setValue: Dispatch<React.SetStateAction<SingleValue<SearchOption> | undefined>>;
  locations: Location[];
  setLocations: Dispatch<React.SetStateAction<Location[]>>;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  setSearchOptions: Dispatch<React.SetStateAction<SearchOption[]>>;
  isShowLocationNearMe: boolean | undefined;
  startDate: Date | undefined;
  updateStartDate: (dateValue: Date | undefined) => void;
  onSearchClick: () => void;
};

export default function SearchBarRedesign({
  isLoading,
  value,
  setValue,
  locations,
  setLocations,
  setIsLoading,
  setSearchOptions,
  isShowLocationNearMe,
  startDate,
  updateStartDate,
  onSearchClick,
}: SearchBarRedesignProps) {
  return (
    <div className="bg-rp-inverted absolute flex flex-col gap-2 max-w-343 p-2 box-content justify-between mt-11 rounded d:box-border d:h-60px d:p-0 d:py-2 d:flex-row d:w-full d:max-w-799 d:gap-0">
      <div className="flex w-343 items-center relative z-20 border-b pb-2 d:pb-0 d:pl-4 d:gap-2 d:border-b-0 d:border-r">
        {!isLoading ? (
          <div className={`w-6 h-6 flex-shrink-0 flex-grow-0 ${styles['basis-0']}`}>
            <Image src={locationPin} width={24} height={24} alt="search-icon" />
          </div>
        ) : (
          <div className="w-6 h-6">
            <SearchSpinner />
          </div>
        )}
        <ReactSelectSearch
          value={value}
          setValue={setValue}
          locations={locations}
          input={value ? value.value : ''}
          setLocations={setLocations}
          setIsLoading={setIsLoading}
          setSearchOptions={setSearchOptions}
          searchWithoutQuery={false}
          isBottomSearchBar={false}
          isShowLocationNearMe={isShowLocationNearMe}
          isNewHomePage
        />
      </div>

      <div className="z-20 w-343 flex gap-2 items-center py-2  d:py-0">
        <Image src={calendarHeaderIcon} alt="Calendar Icon" />
        <ReactDatePicker
          startDate={startDate}
          setStartDate={updateStartDate}
          classes="text-base text-rp-neutral font-rp-basetica w-343"
          showClearDate={false}
          placeholder="Choose date"
          redesign
        />
      </div>
      <div className="z-10 h-full w-343 flex justify-end items-center relative d:pr-2 d:w-88">
        <Button
          size="lg"
          color="primary"
          onClick={onSearchClick}
          fullScreen
          classes="d:absolute d:-ml-14 block d:hidden"
        >
          Search
        </Button>
        <Button
          size="lg"
          color="primary"
          onClick={onSearchClick}
          classes="d:absolute d:-ml-14 hidden d:block"
        >
          Search
        </Button>
      </div>
    </div>
  );
}
